export type NativeDateInput = Date | string | number | null | undefined;

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR;
const TWO_DIGIT_LIMIT = 10;
const DATE_PARTS = 3;
const MONTHS_PER_YEAR = 12;
const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;
const SECONDS_PER_DAY = SECONDS_PER_HOUR * HOURS_PER_DAY;
const DAYS_PER_WEEK = 7;
const SECONDS_PER_WEEK = SECONDS_PER_DAY * DAYS_PER_WEEK;
const AVERAGE_DAYS_PER_MONTH = 30;
const SECONDS_PER_MONTH = SECONDS_PER_DAY * AVERAGE_DAYS_PER_MONTH;
const SECONDS_PER_YEAR = SECONDS_PER_MONTH * MONTHS_PER_YEAR;

const padTwoDigits = (value: number): string => value < TWO_DIGIT_LIMIT ? '0' + value : value.toString();

const normalizeDate = (date: Date): Date | null => Number.isNaN(date.getTime()) ? null : date;

const parseDayMonthYear = (value: string): Date | null => {
  const match = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(value.trim());

  if (match === null) {
    return null;
  }

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
};

export const parseNativeDateInput = (value: NativeDateInput): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return normalizeDate(value);
  }

  if (typeof value === 'number') {
    return normalizeDate(new Date(value));
  }

  const trimmedValue = value.trim();
  const dayMonthYearDate = parseDayMonthYear(trimmedValue);

  if (dayMonthYearDate !== null) {
    return dayMonthYearDate;
  }

  return normalizeDate(new Date(trimmedValue));
};

export const formatNativeDateAsDayMonthYear = (value: NativeDateInput): string => {
  const date = parseNativeDateInput(value);

  if (date === null) {
    throw new Error('formatNativeDateAsDayMonthYear: invalid date value');
  }

  return padTwoDigits(date.getDate()) + '/' + padTwoDigits(date.getMonth() + 1) + '/' + date.getFullYear();
};

export const diffCompleteMonths = (laterValue: NativeDateInput, earlierValue: NativeDateInput): number => {
  const later = parseNativeDateInput(laterValue);
  const earlier = parseNativeDateInput(earlierValue);

  if (later === null || earlier === null) {
    return 0;
  }

  let monthDiff = (later.getFullYear() - earlier.getFullYear()) * 12 + later.getMonth() - earlier.getMonth();

  if (later.getDate() < earlier.getDate()) {
    monthDiff -= 1;
  }

  return monthDiff;
};

export const parseNativeTimeToMinutes = (value: NativeDateInput): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date || typeof value === 'number') {
    const date = parseNativeDateInput(value);

    return date === null ? null : date.getHours() * MINUTES_PER_HOUR + date.getMinutes();
  }

  const trimmedValue = value.trim();
  const timeMatch = /^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/.exec(trimmedValue);

  if (timeMatch !== null) {
    const hours = Number(timeMatch[1]);
    const minutes = Number(timeMatch[2]);

    if (hours >= 0 && hours < HOURS_PER_DAY && minutes >= 0 && minutes < MINUTES_PER_HOUR) {
      return hours * MINUTES_PER_HOUR + minutes;
    }
  }

  const date = parseNativeDateInput(trimmedValue);

  return date === null ? null : date.getHours() * MINUTES_PER_HOUR + date.getMinutes();
};

const formatMinutesAsTime = (totalMinutes: number): string => {
  const normalizedMinutes = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalizedMinutes / MINUTES_PER_HOUR);
  const minutes = normalizedMinutes % MINUTES_PER_HOUR;

  return padTwoDigits(hours) + ':' + padTwoDigits(minutes);
};

export const formatNativeTimeAsHoursMinutes = (value: NativeDateInput): string => {
  const minutes = parseNativeTimeToMinutes(value);

  return minutes === null ? '' : formatMinutesAsTime(minutes);
};

export const isValidNativeTime = (value: NativeDateInput): boolean => parseNativeTimeToMinutes(value) !== null;

export const isNativeEndTimeBeforeStart = (startTime: NativeDateInput, endTime: NativeDateInput): boolean => {
  const startMinutes = parseNativeTimeToMinutes(startTime);
  const endMinutes = parseNativeTimeToMinutes(endTime);

  return startMinutes !== null && endMinutes !== null && endMinutes < startMinutes;
};

export const subtractDurationFromTime = (duration: string, time: string): string => {
  const durationParts = duration.split(':');

  if (durationParts.length < DATE_PARTS - 1) {
    return '00:00';
  }

  const durationMinutes = Number(durationParts[0] ?? 0) * MINUTES_PER_HOUR + Number(durationParts[1] ?? 0);
  const baseMinutes = parseNativeTimeToMinutes(time);

  if (baseMinutes === null || Number.isNaN(durationMinutes)) {
    return '00:00';
  }

  return formatMinutesAsTime(baseMinutes - durationMinutes);
};

export const calculateDurationBetweenTimes = (startTime: string | undefined, endTime: string): string => {
  const startMinutes = parseNativeTimeToMinutes(startTime);
  const endMinutes = parseNativeTimeToMinutes(endTime);

  if (startMinutes === null || endMinutes === null || endMinutes < startMinutes) {
    return '00:00';
  }

  return formatMinutesAsTime(endMinutes - startMinutes);
};


export const diffCompleteDays = (laterValue: NativeDateInput, earlierValue: NativeDateInput): number => {
  const later = parseNativeDateInput(laterValue);
  const earlier = parseNativeDateInput(earlierValue);

  if (later === null || earlier === null) {
    return 0;
  }

  const startOfLater = new Date(later.getFullYear(), later.getMonth(), later.getDate()).getTime();
  const startOfEarlier = new Date(earlier.getFullYear(), earlier.getMonth(), earlier.getDate()).getTime();

  return Math.floor((startOfLater - startOfEarlier) / (SECONDS_PER_DAY * 1000));
};

export const isSameNativeDay = (leftValue: NativeDateInput, rightValue: NativeDateInput): boolean => {
  const left = parseNativeDateInput(leftValue);
  const right = parseNativeDateInput(rightValue);

  return left !== null && right !== null
    && left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
};

export const formatNativeDateAsSpanishLongDateTime = (value: NativeDateInput): string => {
  const date = parseNativeDateInput(value);

  if (date === null) {
    throw new Error('formatNativeDateAsSpanishLongDateTime: invalid date value');
  }

  const month = new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(date);

  return `${padTwoDigits(date.getDate())} de ${month} a las ${date.getHours()}:${padTwoDigits(date.getMinutes())}`;
};

const getRelativeTimeUnit = (absoluteSeconds: number): Intl.RelativeTimeFormatUnit => {
  if (absoluteSeconds < SECONDS_PER_MINUTE) {
    return 'second';
  }

  if (absoluteSeconds < SECONDS_PER_HOUR) {
    return 'minute';
  }

  if (absoluteSeconds < SECONDS_PER_DAY) {
    return 'hour';
  }

  if (absoluteSeconds < SECONDS_PER_WEEK) {
    return 'day';
  }

  if (absoluteSeconds < SECONDS_PER_MONTH) {
    return 'week';
  }

  if (absoluteSeconds < SECONDS_PER_YEAR) {
    return 'month';
  }

  return 'year';
};

const getRelativeTimeDivisor = (unit: Intl.RelativeTimeFormatUnit): number => {
  switch (unit) {
    case 'second':
      return 1;
    case 'minute':
      return SECONDS_PER_MINUTE;
    case 'hour':
      return SECONDS_PER_HOUR;
    case 'day':
      return SECONDS_PER_DAY;
    case 'week':
      return SECONDS_PER_WEEK;
    case 'month':
      return SECONDS_PER_MONTH;
    case 'year':
      return SECONDS_PER_YEAR;
    default:
      return 1;
  }
};

export const formatNativeRelativeTimeFromNow = (value: NativeDateInput, nowValue: NativeDateInput = new Date()): string => {
  const date = parseNativeDateInput(value);
  const now = parseNativeDateInput(nowValue);

  if (date === null || now === null) {
    return '';
  }

  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const absoluteSeconds = Math.abs(seconds);
  const unit = getRelativeTimeUnit(absoluteSeconds);
  const amount = Math.round(seconds / getRelativeTimeDivisor(unit));

  return new Intl.RelativeTimeFormat('es-ES', { numeric: 'auto' }).format(amount, unit);
};
